import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldAlert, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { getMyTotpStatus } from "@/lib/totp.functions";
import { useCurrency } from "@/lib/currency";

const KEY = "wtb-2fa-banner-dismissed-at";
const REPROMPT_MS = 24 * 60 * 60 * 1000;

export function TwoFactorPromptBanner({
  transactions = 0,
  payouts = 0,
}: {
  transactions?: number;
  payouts?: number;
}) {
  const { user } = useAuth();
  const { display, format } = useCurrency();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (transactions <= 10 && payouts <= 500) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await getMyTotpStatus();
        if (cancelled || status.enrolled) return;
        const last = Number(localStorage.getItem(KEY) ?? 0);
        if (Date.now() - last < REPROMPT_MS) return;
        setVisible(true);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [user, transactions, payouts]);

  if (!visible) return null;

  return (
    <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
      <div className="flex-1">
        <p className="font-semibold">Protect your earnings — enable two-factor authentication in Account Settings.</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          You've completed {transactions} transactions and received ${payouts.toLocaleString()} USD{display !== "USD" ? ` (≈ ${format(payouts)})` : ""} in payouts.
        </p>
        <Link to="/account" hash="security" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">
          Go to Security settings →
        </Link>
      </div>
      <button
        onClick={() => {
          try { localStorage.setItem(KEY, String(Date.now())); } catch {}
          setVisible(false);
        }}
        className="rounded p-1 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
